import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { getAllBookings } from '../../../services/bookings';
import LoadingSpinner from '../../shared/LoadingSpinner';
import { User, Mail, Phone, DollarSign, Package, MapPin, Users as UsersIcon } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BookingsCalendar.css';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const BookingsCalendar = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState('month');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    const result = await getAllBookings();
    if (result.success) {
      setBookings(result.data);
    }
    setLoading(false);
  };

  // Convert bookings to calendar events
  const events = useMemo(() => {
    const calendarEvents = [];

    bookings.forEach((booking) => {
      if (booking.items) {
        booking.items.forEach((item) => {
          // Parse dates and add one day to end date for proper display
          const startDate = new Date(item.startDate + 'T00:00:00');
          const endDate = new Date(item.endDate + 'T00:00:00');
          // Add one day to end date so it shows the full last day
          const displayEndDate = addDays(endDate, 1);

          calendarEvents.push({
            id: `${booking.id}-${item.id}`,
            title: `${item.name}`,
            start: startDate,
            end: displayEndDate,
            resource: {
              booking: booking,
              item: item,
              type: item.type,
            },
          });
        });
      }
    });

    return calendarEvents;
  }, [bookings]);

  const getItemIcon = (type) => {
    switch (type) {
      case 'location':
        return <MapPin size={14} />;
      case 'equipment':
        return <Package size={14} />;
      case 'talent':
        return <UsersIcon size={14} />;
      default:
        return <Package size={14} />;
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#d4af37';
    
    switch (event.resource.type) {
      case 'location':
        backgroundColor = '#3b82f6'; // blue
        break;
      case 'equipment':
        backgroundColor = '#8b5cf6'; // purple
        break;
      case 'talent':
        backgroundColor = '#ec4899'; // pink
        break;
      default:
        backgroundColor = '#d4af37'; // gold
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '13px',
        padding: '2px 5px',
      },
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bookings-calendar-container">
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color location"></span>
          <span>Locations</span>
        </div>
        <div className="legend-item">
          <span className="legend-color equipment"></span>
          <span>Equipment</span>
        </div>
        <div className="legend-item">
          <span className="legend-color talent"></span>
          <span>Talent</span>
        </div>
      </div>

      <div className="calendar-wrapper">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          view={view}
          onView={setView}
          views={['month', 'week', 'day', 'agenda']}
          popup
        />
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="calendar-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Booking Details</h2>
              <button onClick={() => setSelectedEvent(null)} className="modal-close">
                ×
              </button>
            </div>

            <div className="event-modal-content">
              {/* Item Details */}
              <div className="event-section">
                <h3 className="event-section-title">Item Details</h3>
                <div className="event-item-card">
                  <div className="event-item-header">
                    <div className="item-type-badge">
                      {getItemIcon(selectedEvent.resource.type)}
                      <span>{selectedEvent.resource.type}</span>
                    </div>
                    <span className="item-price">
                      ${selectedEvent.resource.item.total?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="item-name">{selectedEvent.resource.item.name}</div>
                  <div className="item-dates">
                    {formatDate(selectedEvent.resource.item.startDate)} -{' '}
                    {formatDate(selectedEvent.resource.item.endDate)}
                  </div>
                  <div className="item-days">
                    {selectedEvent.resource.item.days} day
                    {selectedEvent.resource.item.days !== 1 ? 's' : ''} @ $
                    {selectedEvent.resource.item.price}/day
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="event-section">
                <h3 className="event-section-title">Customer Information</h3>
                <div className="event-info-grid">
                  <div className="info-item">
                    <User size={16} />
                    <div>
                      <div className="info-label">Name</div>
                      <div className="info-value">
                        {selectedEvent.resource.booking.customerName}
                      </div>
                    </div>
                  </div>
                  <div className="info-item">
                    <Mail size={16} />
                    <div>
                      <div className="info-label">Email</div>
                      <div className="info-value">
                        {selectedEvent.resource.booking.customerEmail}
                      </div>
                    </div>
                  </div>
                  {selectedEvent.resource.booking.customerPhone && (
                    <div className="info-item">
                      <Phone size={16} />
                      <div>
                        <div className="info-label">Phone</div>
                        <div className="info-value">
                          {selectedEvent.resource.booking.customerPhone}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Booking Info */}
              <div className="event-section">
                <h3 className="event-section-title">Booking Information</h3>
                <div className="event-info-grid">
                  <div className="info-item">
                    <DollarSign size={16} />
                    <div>
                      <div className="info-label">Total Amount</div>
                      <div className="info-value" style={{ color: 'var(--color-primary)' }}>
                        ${selectedEvent.resource.booking.totalAmount?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                  </div>
                  <div className="info-item">
                    <Package size={16} />
                    <div>
                      <div className="info-label">Status</div>
                      <div className="info-value">
                        <span
                          className={`status-badge ${
                            selectedEvent.resource.booking.status || 'confirmed'
                          }`}
                        >
                          {selectedEvent.resource.booking.status || 'confirmed'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="booking-id-section">
                  <span className="info-label">Booking ID:</span>
                  <span className="booking-id-value">{selectedEvent.resource.booking.id}</span>
                </div>
              </div>

              {selectedEvent.resource.booking.notes && (
                <div className="event-section">
                  <h3 className="event-section-title">Notes</h3>
                  <div className="notes-box">{selectedEvent.resource.booking.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsCalendar;